import { EventEmitter, Injectable, inject } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpRequest, HttpResponse } from "@angular/common/http";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, last, map, tap } from "rxjs/operators";

import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { MediaModel, UPL_STATE } from "../models";
import { FileEvent, FileEventType } from "../components";
import { NotificationService } from "libs/tm-common/src/lib/services/notification.lib.service";
import { TreatmentModel } from "libs/treatments/src/lib/models";

export type TransferEvent =
	{ event: UPL_STATE.PERCENT, payload: number }
	| { event: UPL_STATE.COMPLETED, payload: MediaModel | WebviewerResponsePayload }
	| { event: UPL_STATE.ERROR, payload: {orgFilename: string} };

export type WebviewerResponsePayload = {media: MediaModel, treatment: TreatmentModel, webviewer: string}

@Injectable({
	providedIn: 'root'
})
export class MediaService {
	private httpClient = inject(HttpClient);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);
	private notificationService = inject(NotificationService);

	/**
	 * Fetches a list of media files regarding the treatmentId as well
	 * as the media files of associated treatments
	 */
	getMediaFiles(treatmentId: string, fetchFromAssociatedTreatments: boolean) : Observable<MediaModel[]> {
		const params = {
			treatmentId: treatmentId,
			fetchFromAssociatedTreatments: fetchFromAssociatedTreatments
		}
		return this.httpClient.get<MediaModel[]>(`${this.config.apiUrl}/media`, {
			params,
			withCredentials: true
		});
	}

	getMediaFilesByTicketId(ticketId: string) : Observable<MediaModel[]> {
		const params = {
			ticketId: ticketId
		}
		return this.httpClient.get<MediaModel[]>(`${this.config.apiUrl}/media`, {
			params,
			withCredentials: true
		});
	}

	getMediaFilesCustomerPaginated(
			skip: number,
			limit: number,
			textFilter: string,
			activeSortFilter: string,
			sortDirection: string
			): Observable<{media: Partial<MediaModel>[], count: number}>{
			const params = {
				skip: skip,
				limit: limit,
			};

			if(textFilter){
				params["textFilter"] = textFilter;
			}
			if(activeSortFilter && sortDirection){
				params["activeSortFilter"] = activeSortFilter;
				params["sortDirection"] = sortDirection;
			}
			const options = {
				params: params,
				withCredentials: true
			};
			return this.httpClient.get<{media: Partial<MediaModel>[], count: number}>( `${this.config.apiUrl}/media/customer/paginated`, options);
		}

		getMediaFilesAdminPaginated(
			skip: number,
			limit: number,
			textFilter: string,
			activeSortFilter: string,
			sortDirection: string,
			customerId?: string,
			): Observable<{media: Partial<MediaModel>[], count: number}>{
			const params = {
				skip: skip,
				limit: limit,
			};

			if(textFilter){
				params["textFilter"] = textFilter;
			}
			if(customerId){
				params["customerId"] = customerId;
			}

			if(activeSortFilter && sortDirection){
				params["activeSortFilter"] = activeSortFilter;
				params["sortDirection"] = sortDirection;
			}

			const options = {
				params: params,
				withCredentials: true
			};
			return this.httpClient.get<{media: Partial<MediaModel>[], count: number}>( `${this.config.apiUrl}/media/admin/paginated`, options);
		}

	deleteFile(id) {
		return this.httpClient.delete(`${this.config.apiUrl}/media/${id}`, {
			withCredentials: true
		});
	}

	uploadFile(
		file: File,
		params: { treatment: string },
		triggerOrderStatus: boolean = true,
		transferStatus$: EventEmitter<any>
	): Observable<MediaModel> {
		const formData: FormData = new FormData();
		// submit each param as form field
		Object.keys(params).forEach((prop) => {
			formData.append(prop, params[prop]);
		});

		formData.append("file", file, file.name);
		// unfortunately FormData is not capable of transmitting 'boolean' values
		// therefore we transfer '0' or '1' representing the boolean pendant
		formData.append("triggerOrderStatus", triggerOrderStatus ? '1' : '0' );

		const url = `${this.config.apiUrl}/upload`;

		return this.executeHttpPostRequest(url, formData, file, transferStatus$);
	}

	uploadFileAnamnese(
		file: File,
		patientId: string,
		uploadType: string,
		transferStatus$: EventEmitter<TransferEvent>
	): Observable<MediaModel> {
		const formData: FormData = new FormData();
		formData.append("patientId", patientId);
		if (uploadType) {
			formData.append("uploadType", uploadType);
		}
		formData.append("file", file, file.name);

		const url = `${this.config.apiUrl}/anamnese/media`;

		return this.executeHttpPostRequest(url, formData, file, transferStatus$);
	}

	uploadFileTicket(
		file: File,
		ticketId: string,
		transferStatus$: EventEmitter<TransferEvent>
	): Observable<MediaModel> {
		const formData: FormData = new FormData();
		formData.append("ticketId", ticketId);
		formData.append("file", file, file.name);

		const url = `${this.config.apiUrl}/tickets/media`;

		return this.executeHttpPostRequest(url, formData, file, transferStatus$);
	}



	uploadWebviewerFile(
		file: File,
		params: { treatmentId: string },
		transferStatus$: EventEmitter<TransferEvent>
	): Observable<MediaModel> {
		const formData: FormData = new FormData();
		formData.append("treatment", params.treatmentId);
		formData.append("file", file, file.name);

		const url = `${this.config.apiUrl}/media/webviewer`;

		return this.executeHttpPostRequest(url, formData, file, transferStatus$);
	}

	private executeHttpPostRequest(url: string, formData:FormData, file: File, transferStatus$: EventEmitter<TransferEvent>): Observable<MediaModel>{
		const req = new HttpRequest("POST", url, formData, {
			withCredentials: true,
			reportProgress: true
		});

		return this.httpClient.request(req).pipe(
			tap((httpEvent: HttpEvent<any>) => this.processHttpEvent(httpEvent, file, transferStatus$)),
			last((httpEvent: HttpEvent<MediaModel>) => httpEvent.type === HttpEventType.Response), // return last (completed) message to caller
			map((httpEvent: HttpEvent<MediaModel>) => (httpEvent as HttpResponse<MediaModel>).body),
			catchError((error) => this.handleError(error, file, transferStatus$))
		);
	}

	updateMedium(mediaId: string, requestBody){
		const url = this.config.apiUrl;
		const req = new HttpRequest("PATCH", `${url}/media/${mediaId}`, requestBody, {
			withCredentials: true,
		});
		return this.httpClient.request(req).subscribe({
			error: error => console.error("Error caught while updating medium", error)
		});
	}

	getFile(id: string, filename: string, showErrorNotification: boolean = true): Observable<FileEvent> {
		const url = `${this.config.apiUrl}/media/${id}`;

		// Verify existence first via HEAD, then let the browser handle the
		// download natively so it appears in the browser's download manager with
		// real progress tracking from the start.
		return this.httpClient.head(url, { withCredentials: true, observe: "response" }).pipe(
			map(() => {
				const a = document.createElement("a");
				a.href = url;
				a.style.display = "none";
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				return { type: FileEventType.DOWNLOADED, payload: "" } as FileEvent;
			}),
			catchError((error) => {
				console.error("Error caught while retrieving file", error);
				if(showErrorNotification) {
					this.notificationService.showErrorMessage("fileNotFound", filename, 10000);
				}
				return of({ type: FileEventType.FILE_NOT_FOUND, payload: "fileNotFound" } as FileEvent);
			})
		);
	}

	getFileAsBlob(id) : Observable<Blob> {
		const httpOptions: object = {
			responseType: "blob",
			observe: "body",
			withCredentials: true
		};
		return this.httpClient.get<Blob>(`${this.config.apiUrl}/media/${id}`, httpOptions);
	}

	getFileAsObject(mediaId: string): Observable<any> {
		return this.httpClient
			.get(`${this.config.apiUrl}/media/${mediaId}`, { withCredentials: true });
	}

	/**
	 * Process the HTTP event (and return a message for debugging purposes)
	 * @param httpEvent - The HTTP event to process.
	 * @param file - The file being uploaded.
	 * @param transferStatus - The event emitter to emit the transfer status.
	 * @returns A message describing the event.
	 */
	private processHttpEvent(httpEvent: HttpEvent<any>, file: File, transferStatus: EventEmitter<TransferEvent>): string {
		switch (httpEvent.type) {
			case HttpEventType.Sent:
				return `Uploading file "${file.name}" of size ${file.size}.`;

			case HttpEventType.UploadProgress: {
				// Compute and show the % done:
				const percentDone = Math.round((100 * httpEvent.loaded) / httpEvent.total);
				transferStatus.emit({
					event: UPL_STATE.PERCENT,
					payload: percentDone,
				});
				return `File "${file.name}" is ${percentDone}% uploaded.`;
			}

			case HttpEventType.Response:
				transferStatus.emit({
					event: UPL_STATE.COMPLETED,
					payload: httpEvent.body
				});
				return `File "${file.name}" was completely uploaded!`;

			case HttpEventType.DownloadProgress:
				return `DownloadProgress file "${file.name}" of size ${file.size}.`;

			case HttpEventType.ResponseHeader:
				return `File "${file.name}" ${httpEvent.status} ${httpEvent.statusText}`;

			default:
				console.error("getEventMessage UNKNOWN", httpEvent);
				// this.transferStatus.emit({event: UPL_STATE.ERROR, payload: `${HttpEventType[event.type]}`});
				return `File "${file.name}" surprising upload event: ${
					HttpEventType[httpEvent.type]
				}.`;
		}
	}

	private handleError(
		error: HttpErrorResponse,
		file: File,
		transferStatus: EventEmitter<TransferEvent>
	) {
		console.error(
			`Upload failed for "${file.name}" — HTTP ${error.status}: ${error.message}`,
			error
		);
		transferStatus.emit({ event: UPL_STATE.ERROR, payload: { orgFilename: file.name } });
		return EMPTY;
	}

	getFileSizeAsString(bytes: number) : string {
		if (bytes < 1024) {
			return `${bytes.toLocaleString(this.config.locale)} B`;
		} else if (bytes < 1024 * 1024) {
			return `${this.getFileSizeKb(bytes)} KB`;
		} else {
			return `${this.getFileSizeMb(bytes)} MB`;
		}
	}

	getFileSizeKb(bytes: number) : string {
		return (bytes / 1024).toLocaleString(this.config.locale, {
			maximumFractionDigits: 2
		});
	}

	getFileSizeMb(bytes: number) : string {
		return (bytes / (1024 * 1024)).toLocaleString(this.config.locale, {
			maximumFractionDigits: 2
		});
	}
}
